// TransactionConfigCells — presentation cells for `transactionConfig`.
//
// Split out of `transactionConfig.tsx` so that module exports only config
// values and declares no components (react-refresh/only-export-components).
//
// `TransitEditCell` reads `transactionConfig.schema` at RENDER time (not at
// module evaluation), so the config ↔ cells import cycle is safe — same
// pattern as `sections/InvoiceLineItems.columns.tsx` in the invoices tree.

import React from "react";

import type { Transaction } from "@shared/types/transaction";
import { parseTransitCountries, formatTransitCountries } from "@shared/services/transaction/helpers";
import { getSchemaMeta } from "@ds/config/schema";
import { useEntityFieldEdit } from "@ds/actions/useEntityFieldEdit";
import { useCountries } from "@ds/connected/useCountries";
import { Editable } from "@ds/editable/Editable";
import { EmptyValue } from "@ds/display/EmptyValue";
import { TextValue } from "@ds/display/TextValue";
import { Pill } from "@ds/display/Pill";
import { Inline } from "@ds/layout/Inline";

import { transactionConfig } from "../transactionConfig";
import { useTransactionWorkflow } from "../hooks/transactionWorkflowContext";
import { dateRangeShort } from "../transactionConfigDates";

export const WasteCodeCell: React.FC<{ value: string | undefined }> = ({ value }) =>
  value ? <TextValue as="span">{value}</TextValue> : <EmptyValue />;

export const PickupDateRangeCell: React.FC<{ from: string | null | undefined; to: string | null | undefined }> = ({ from, to }) =>
  <>{dateRangeShort(from, to)}</>;

const TransitCountries: React.FC<{ csv: string | null | undefined }> = ({ csv }) => {
  const { getCountryName } = useCountries();
  const codes = parseTransitCountries(csv);
  if (!codes) return <EmptyValue />;
  return (
    <Inline as="span" inline gap="xs" wrap>
      {codes.map((c) => (
        <Pill key={c} tone="grey" variant="outline" size="sm">
          {getCountryName(c)}
        </Pill>
      ))}
    </Inline>
  );
};

export const TransitEditCell: React.FC<{ tx: Transaction }> = ({ tx }) => {
  const { refetchAll } = useTransactionWorkflow();
  const onFieldEdit = useEntityFieldEdit(getSchemaMeta(transactionConfig.schema), tx.id, {
    onSuccess: refetchAll,
  });
  const { euCountryOptions } = useCountries();
  const arr = parseTransitCountries(tx.transit) ?? [];
  return (
    <Editable<string[]>
      name="transit"
      value={arr}
      field={{ type: "multiselect", options: euCountryOptions }}
      display={() => <TransitCountries csv={tx.transit} />}
      onChange={async (v) => {
        await onFieldEdit("", "transit", formatTransitCountries(v));
      }}
    />
  );
};
